import React from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { offersAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import HostDashboard from './HostDashboard';
import OrderAnalytics from '../components/dashboard/OrderAnalytics';
import ActivityAnalytics from '../components/dashboard/ActivityAnalytics';
import { OffertaStats, MeseStats, TopClienteStats, AgentDashboardStats } from '../types';
import { format, parseISO, subDays, subMonths, startOfMonth, addMonths, differenceInCalendarMonths } from 'date-fns';
import { it } from 'date-fns/locale';
import toast from 'react-hot-toast';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ComposedChart, Line, Legend, Cell,
} from 'recharts';
import {
  DocumentTextIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  CurrencyEuroIcon,
  ChartBarSquareIcon,
  ArrowTrendingUpIcon,
  InboxStackIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { formatCurrency, formatCurrencyCompact, STATUS_COLORS, STATUS_LABELS } from '../utils/constants';

function fillMonthGaps(data: MeseStats[], fromDate: string, toDate: string): (MeseStats & { label: string })[] {
  const months: (MeseStats & { label: string })[] = [];
  let d = startOfMonth(parseISO(fromDate));
  const last = startOfMonth(parseISO(toDate));
  // Guard: la finestra è clampata a 36 mesi dal filtro, ma non si sa mai.
  for (let i = 0; i < 48 && d <= last; i += 1) {
    const key = format(d, 'yyyy-MM');
    const label = format(d, 'MMM yy', { locale: it });
    const existing = data.find(m => m.mese === key);
    months.push({
      mese: key,
      label,
      totale_offerte: existing?.totale_offerte ?? 0,
      valore_totale: existing?.valore_totale ?? 0,
      valore_accettate: existing?.valore_accettate ?? 0,
    });
    d = addMonths(d, 1);
  }
  return months;
}

const SkeletonCard: React.FC = () => (
  <div className="bg-white p-6 rounded-lg shadow">
    <div className="flex items-center">
      <div className="flex-shrink-0">
        <div className="animate-pulse bg-gray-200 h-8 w-8 rounded" />
      </div>
      <div className="ml-4 space-y-2">
        <div className="animate-pulse bg-gray-200 h-4 w-20 rounded" />
        <div className="animate-pulse bg-gray-200 h-7 w-16 rounded" />
      </div>
    </div>
  </div>
);

const SkeletonChart: React.FC<{ height?: string }> = ({ height = 'h-64' }) => (
  <div className={`animate-pulse bg-gray-100 ${height} rounded`} />
);

const EmptyChart: React.FC<{ message?: string }> = ({ message = 'Nessun dato disponibile' }) => (
  <div className="flex items-center justify-center h-64 text-gray-400 text-sm">
    {message}
  </div>
);

type DashboardTab = 'offerte' | 'attivita' | 'ordinato';

/** Ampiezza massima della finestra del filtro periodo (mesi). */
const MAX_SPAN_MONTHS = 36;

const TABS: { key: DashboardTab; label: string }[] = [
  { key: 'offerte', label: 'Panoramica Offerte' },
  { key: 'attivita', label: 'Attività' },
  { key: 'ordinato', label: 'Ordinato' },
];

const TenantDashboard: React.FC = () => {
  const { user } = useAuth();

  // Redirect cliente users to catalog
  if (user?.role === 'cliente') {
    return <Navigate to="/catalog" replace />;
  }

  // Filtro periodo condiviso dalle tab. Default ultimi
  // 30 giorni; ampiezza massima 36 mesi (oltre, il bound opposto viene spostato).
  const [fromDate, setFromDate] = React.useState(() => format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [toDate, setToDate] = React.useState(() => format(new Date(), 'yyyy-MM-dd'));

  const applyRange = (field: 'from' | 'to', value: string) => {
    if (!value) return;
    let from = field === 'from' ? value : fromDate;
    let to = field === 'to' ? value : toDate;
    if (parseISO(from) > parseISO(to)) {
      if (field === 'from') to = from;
      else from = to;
    }
    if (differenceInCalendarMonths(parseISO(to), parseISO(from)) > MAX_SPAN_MONTHS) {
      toast(`Intervallo massimo: ${MAX_SPAN_MONTHS} mesi`);
      if (field === 'from') to = format(addMonths(parseISO(from), MAX_SPAN_MONTHS), 'yyyy-MM-dd');
      else from = format(subMonths(parseISO(to), MAX_SPAN_MONTHS), 'yyyy-MM-dd');
    }
    setFromDate(from);
    setToDate(to);
  };

  const { data: statsData, isLoading: isLoadingStats } = useQuery({
    queryKey: ['offerte', 'stats', fromDate, toDate],
    queryFn: async () => {
      const res = await offersAPI.getStats({ from_date: fromDate, to_date: toDate });
      return res.data;
    },
  });

  const { data: offersData, isLoading: isLoadingOffers } = useQuery({
    queryKey: ['offers', 'dashboard'],
    queryFn: () => offersAPI.getOffers({ limit: 10, page: 1 }),
  });

  const isAgent = user?.role === 'commerciale' || user?.role === 'manager';
  const { data: agentData, isLoading: isLoadingAgent } = useQuery({
    queryKey: ['agent', 'dashboard'],
    queryFn: async () => {
      const res = await offersAPI.getAgentDashboard();
      return res.data as AgentDashboardStats;
    },
    enabled: isAgent,
  });

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab: DashboardTab = TABS.find(tab => tab.key === searchParams.get('tab'))?.key ?? 'offerte';
  const setActiveTab = (tab: DashboardTab) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab === 'offerte') next.delete('tab');
        else next.set('tab', tab);
        return next;
      },
      { replace: true },
    );
  };

  const stats: OffertaStats | undefined = statsData;
  const offers = offersData?.data?.data || [];

  const valorePerStatoData = React.useMemo(() => {
    if (!stats?.valore_per_stato) return [];
    return ['bozza', 'inviata', 'accettata', 'rifiutata', 'scaduta']
      .filter(s => (stats.valore_per_stato[s] ?? 0) > 0 || (stats.per_stato[s] ?? 0) > 0)
      .map(s => ({
        statoKey: s,
        stato: STATUS_LABELS[s] || s,
        valore: stats.valore_per_stato[s] ?? 0,
        fill: STATUS_COLORS[s] || '#9CA3AF',
      }));
  }, [stats]);

  const trendData = React.useMemo(() => {
    return fillMonthGaps(stats?.trend_mensile || [], fromDate, toDate);
  }, [stats, fromDate, toDate]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'bozza':
        return <DocumentTextIcon className="h-5 w-5 text-gray-500" />;
      case 'inviata':
        return <ClockIcon className="h-5 w-5 text-blue-500" />;
      case 'accettata':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'rifiutata':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'scaduta':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      default:
        return <DocumentTextIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'bozza': return 'bg-gray-100 text-gray-800';
      case 'inviata': return 'bg-blue-100 text-blue-800';
      case 'accettata': return 'bg-green-100 text-green-800';
      case 'rifiutata': return 'bg-red-100 text-red-800';
      case 'scaduta': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const kpiCards = [
    {
      label: 'Totale Offerte',
      value: stats?.totale_offerte ?? 0,
      format: (v: number) => v.toString(),
      icon: <DocumentTextIcon className="h-8 w-8 text-gray-400" />,
      link: '/offers',
    },
    {
      label: 'Valore Accettate',
      value: stats?.importo_totale_accettate ?? 0,
      format: formatCurrency,
      icon: <CurrencyEuroIcon className="h-8 w-8 text-green-400" />,
      link: '/offers?stato=accettata',
    },
    {
      label: 'Importo Medio',
      value: stats?.importo_medio ?? 0,
      format: formatCurrency,
      icon: <ChartBarSquareIcon className="h-8 w-8 text-blue-400" />,
    },
    {
      label: 'Tasso Conversione',
      value: stats?.tasso_conversione ?? 0,
      format: (v: number) => `${v.toFixed(1)}%`,
      icon: <ArrowTrendingUpIcon className="h-8 w-8 text-emerald-400" />,
    },
    {
      label: 'In Bozza',
      value: stats?.per_stato?.bozza ?? 0,
      format: (v: number) => v.toString(),
      icon: <DocumentTextIcon className="h-8 w-8 text-gray-400" />,
      link: '/offers?stato=bozza',
    },
    {
      label: 'Rifiutate',
      value: stats?.per_stato?.rifiutata ?? 0,
      format: (v: number) => v.toString(),
      icon: <XCircleIcon className="h-8 w-8 text-red-400" />,
      link: '/offers?stato=rifiutata',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Ciao, {user?.name}!
          </h1>
          <p className="text-gray-600 mt-1">
            {activeTab === 'offerte'
              ? 'Ecco il riepilogo delle tue offerte'
              : activeTab === 'ordinato' ? 'Andamento giornaliero e classifiche dell’ordinato per valore' : 'Analisi delle attività: visite, appuntamenti e follow-up'}
          </p>
        </div>
      </div>

      {/* Filtro periodo condiviso */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 flex items-end gap-3 flex-wrap">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Dal</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => applyRange('from', e.target.value)}
            className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Al</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => applyRange('to', e.target.value)}
            className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <p className="text-xs text-gray-400 pb-2">Statistiche e grafici limitati al periodo selezionato (max {MAX_SPAN_MONTHS} mesi).</p>
      </div>

      {/* Tab bar */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'ordinato' && <OrderAnalytics fromDate={fromDate} toDate={toDate} />}

      {activeTab === 'attivita' && <ActivityAnalytics fromDate={fromDate} toDate={toDate} />}

      {activeTab === 'offerte' && (
      <>
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {isLoadingStats ? (
          Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
        ) : (
          kpiCards.map((card) => {
            const inner = (
              <div className="flex items-center">
                <div className="flex-shrink-0">{card.icon}</div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">{card.label}</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {card.format(card.value)}
                  </p>
                </div>
              </div>
            );
            return card.link ? (
              <div
                key={card.label}
                className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(card.link!)}
              >
                {inner}
              </div>
            ) : (
              <div key={card.label} className="bg-white p-6 rounded-lg shadow">
                {inner}
              </div>
            );
          })
        )}
      </div>

      {/* Valore Offerte per Stato */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Valore Offerte per Stato</h3>
        {isLoadingStats ? (
          <SkeletonChart />
        ) : valorePerStatoData.length === 0 ? (
          <EmptyChart />
        ) : (
          <ResponsiveContainer width="100%" height={250}>
            <BarChart
              data={valorePerStatoData}
              layout="vertical"
              margin={{ left: 20, right: 30 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tickFormatter={(v) => formatCurrencyCompact(v)} />
              <YAxis
                type="category"
                dataKey="stato"
                width={90}
                tick={({ x, y, payload }: any) => {
                  const entry = valorePerStatoData.find(d => d.stato === payload.value);
                  return (
                    <text
                      x={x}
                      y={y}
                      dy={4}
                      textAnchor="end"
                      fill="#374151"
                      fontSize={12}
                      style={{ cursor: 'pointer' }}
                      onClick={() => entry && navigate(`/offers?stato=${entry.statoKey}`)}
                    >
                      {payload.value}
                    </text>
                  );
                }}
              />
              <Tooltip formatter={(value) => formatCurrency(Number(value))} labelStyle={{ fontWeight: 600 }} />
              <Bar
                dataKey="valore"
                name="Valore"
                radius={[0, 4, 4, 0]}
                cursor="pointer"
                onClick={(_data: any, index: number) => {
                  const entry = valorePerStatoData[index];
                  if (entry) navigate(`/offers?stato=${entry.statoKey}`);
                }}
              >
                {valorePerStatoData.map((entry, idx) => (
                  <Cell key={idx} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Trend Mensile + Top Clienti */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Trend Mensile */}
        <div className="lg:col-span-3 bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Trend Mensile</h3>
          {isLoadingStats ? (
            <SkeletonChart height="h-72" />
          ) : trendData.every(m => m.valore_totale === 0) ? (
            <EmptyChart />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={trendData} margin={{ left: 10, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={(v) => formatCurrencyCompact(v)} tick={{ fontSize: 12 }} />
                <Tooltip
                  formatter={(value, name) => [
                    formatCurrency(Number(value)),
                    name === 'valore_totale' ? 'Valore Totale' : 'Accettate',
                  ]}
                  labelFormatter={(label) => label}
                />
                <Legend
                  formatter={(value: string) =>
                    value === 'valore_totale' ? 'Valore Totale' : 'Accettate'
                  }
                />
                <Bar dataKey="valore_totale" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                <Line
                  dataKey="valore_accettate"
                  stroke="#10B981"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Top Clienti */}
        <div className="lg:col-span-2 bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Top Clienti per Valore</h3>
          {isLoadingStats ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="animate-pulse bg-gray-200 w-6 h-6 rounded-full" />
                    <div className="space-y-1">
                      <div className="animate-pulse bg-gray-200 h-4 w-28 rounded" />
                      <div className="animate-pulse bg-gray-200 h-3 w-16 rounded" />
                    </div>
                  </div>
                  <div className="animate-pulse bg-gray-200 h-4 w-20 rounded" />
                </div>
              ))}
            </div>
          ) : !stats?.top_clienti?.length ? (
            <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
              Nessun cliente trovato
            </div>
          ) : (
            <div className="space-y-3">
              {stats.top_clienti.map((cliente: TopClienteStats, idx: number) => (
                <div key={cliente.cliente_id} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-xs font-bold flex items-center justify-center">
                      {idx + 1}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-gray-900 truncate max-w-[180px]">
                        {cliente.nome}
                      </p>
                      <p className="text-xs text-gray-500">
                        {cliente.numero_offerte} offert{cliente.numero_offerte === 1 ? 'a' : 'e'}
                      </p>
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                    {formatCurrency(cliente.valore_totale)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Agent Customer Orders Widget */}
      {isAgent && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Pending Orders Count */}
          <div className="lg:col-span-2 bg-white shadow rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <InboxStackIcon className="h-6 w-6 text-amber-500" />
              <h3 className="text-lg font-medium text-gray-900">Ordini Clienti</h3>
            </div>
            {isLoadingAgent ? (
              <div className="animate-pulse space-y-3">
                <div className="h-12 bg-gray-200 rounded" />
                <div className="h-4 w-32 bg-gray-200 rounded" />
              </div>
            ) : (
              <div>
                <p className="text-4xl font-bold text-amber-600">
                  {agentData?.pending_customer_orders ?? 0}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Ordini in bozza da revisionare
                </p>
              </div>
            )}
          </div>

          {/* Recent Customer Orders */}
          <div className="lg:col-span-3 bg-white shadow rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <UserGroupIcon className="h-6 w-6 text-blue-500" />
              <h3 className="text-lg font-medium text-gray-900">Ultimi Ordini dai Clienti</h3>
            </div>
            {isLoadingAgent ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="animate-pulse bg-gray-200 h-4 w-48 rounded" />
                    <div className="animate-pulse bg-gray-200 h-4 w-20 rounded" />
                  </div>
                ))}
              </div>
            ) : !agentData?.recent_customer_orders?.length ? (
              <p className="text-sm text-gray-400">Nessun ordine dai clienti</p>
            ) : (
              <div className="space-y-3">
                {agentData.recent_customer_orders.slice(0, 5).map((order) => (
                  <div key={order.id} className="flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      {getStatusIcon(order.stato)}
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {order.numero} - {order.titolo}
                        </p>
                        <p className="text-xs text-gray-500">{order.cliente_nome}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.stato)}`}>
                        {order.stato.charAt(0).toUpperCase() + order.stato.slice(1)}
                      </span>
                      <span className="text-xs text-gray-500">
                        {new Date(order.created_at).toLocaleDateString('it-IT')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Recent Offers */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Offerte Recenti
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Le tue ultime 10 offerte create
          </p>
        </div>
        <div className="border-t border-gray-200">
          {isLoadingOffers ? (
            <div className="p-4 text-center text-gray-500">Caricamento...</div>
          ) : offers.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              Nessuna offerta trovata. Crea la tua prima offerta!
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {offers.map((offer) => (
                <li key={offer.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3">
                        {getStatusIcon(offer.stato)}
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {offer.numero} - {offer.titolo}
                        </p>
                      </div>
                      <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                        <span>{offer.cliente?.ragione_sociale || offer.cliente?.nome}</span>
                        <span>&bull;</span>
                        <span>{formatCurrency(offer.importo_totale)}</span>
                        <span>&bull;</span>
                        <span>{new Date(offer.created_at).toLocaleDateString('it-IT')}</span>
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(offer.stato)}`}>
                        {offer.stato.charAt(0).toUpperCase() + offer.stato.slice(1)}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      </>
      )}
    </div>
  );
};

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  return user?.is_host ? <HostDashboard /> : <TenantDashboard />;
};
export default Dashboard;
